import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useMobile from 'ui/Datatable/mobile'
import { useDesktop } from 'use/media'

// Const
import { PATH_TICKET_LIST_CLOSED, PATH_TICKET_VIEW } from 'routes/paths'
import { EVENT_TICKET_CREATED } from 'constants/events'

// Services
import event from 'services/event'

// Use
import useMounted from 'use/mounted'

// Api
import { getListOpenTicket, hasClosedTickets } from 'api/ticket'

// Components
import Space from 'ui/Space'
import Text from 'ui/Text'
import Button from 'ui/Button'
import Scrollable from 'ui/Scrollable'
import Box from 'ui/Box'
import Loading from 'ui/Loading'
import Empty from 'ui/Empty'
import Link from 'ui/Link'
import IconSend from 'ui/Icons/IconSend'
import IconArchiveBox from 'ui/Icons/IconArchiveBox'

function ListOpenTickets() {
  // Use hook
  const isMounted = useMounted()
  const isDesktop = useDesktop()
  const isMobile = useMobile()

  // State
  const [loading, setLoading] = useState(false)
  const [openTickets, setOpenTickets] = useState([])
  const [isHasClosedTickets, setIsHasClosedTickets] = useState(false)
  const [isLasted, setIsLastedLasted] = useState(false)

  // Max Height scrollable
  const ticketFormEl = document.getElementById('form-create-ticket')
  const maxHeight = useMemo(() => {
    return ticketFormEl ? ticketFormEl.offsetHeight - 70 : 500
  }, [ticketFormEl])

  // Call api get open tickets
  const getOpenTickets = useCallback(
    async (lastId = null) => {
      setLoading(true)

      try {
        const { list, last } = await getListOpenTicket(lastId)

        if (isMounted.current) {
          if (lastId) {
            setOpenTickets((pre) => [...pre].concat(list))
          } else {
            setOpenTickets(list)
          }
          setIsLastedLasted(last)
        }
      } catch (error) {
        //
      } finally {
        if (isMounted.current) {
          setLoading(false)
        }
      }
    },
    [isMounted]
  )

  // Check has closed tickets
  useEffect(() => {
    hasClosedTickets().then((result) => {
      if (isMounted.current) {
        setIsHasClosedTickets(result)
      }
    })
  }, [isMounted])

  // Get list open ticket in first time
  useEffect(() => {
    getOpenTickets()

    event.on(EVENT_TICKET_CREATED, getOpenTickets)

    return () => {
      event.off(EVENT_TICKET_CREATED, getOpenTickets)
    }
  }, [getOpenTickets])

  // Lazy load more
  const onScrollBottom = async () => {
    if (!isLasted && !loading) {
      await getOpenTickets(openTickets[openTickets.length - 1].id)
    }
  }

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <IconSend />
          <Text bold color="#000000">
            Open tickets
          </Text>
        </Space>
        {isHasClosedTickets && (
          <Link path={PATH_TICKET_LIST_CLOSED}>
            <Button iconLeft={<IconArchiveBox />} underline variant="link">
              Closed tickets
            </Button>
          </Link>
        )}
      </Space>

      <Loading spinning={loading}>
        <Scrollable
          maxHeight={maxHeight}
          onScrollBottom={onScrollBottom}
          style={{ paddingRight: isDesktop ? 10 : 0 }}
        >
          <Space style={{ width: '100%' }} direction="vertical" size={10}>
            {openTickets.length ? (
              openTickets.map((ticket) => (
                <Box
                  key={ticket.id}
                  backgroundColor="rgba(224, 240, 255, 0.35)"
                  style={{
                    borderRadius: 5,
                    border: '1px solid rgba(92, 173, 250, 0.35)',
                    padding: '22px 17px',
                  }}
                >
                  <Text color="#222222" bold>
                    {ticket.category}
                  </Text>
                  <Space
                    align="top"
                    style={{
                      width: '100%',
                      justifyContent: 'space-between',
                      flexFlow: isMobile ? 'column wrap' : 'row wrap',
                    }}
                  >
                    <table style={{ fontSize: 14, marginBottom: 10 }}>
                      <tbody style={{ verticalAlign: 'top' }}>
                        <tr>
                          <td style={{ minWidth: 73 }}>Ticket no.:</td>
                          <td>{ticket.ticketNo}</td>
                        </tr>
                        <tr>
                          <td style={{ minWidth: 73 }}>Date sent:</td>
                          <td>{ticket.createdAt}</td>
                        </tr>
                        <tr>
                          <td style={{ minWidth: 73 }}>Category:</td>
                          <td>{ticket.category}</td>
                        </tr>
                        <tr>
                          <td style={{ minWidth: 73 }}>Type:</td>
                          <td>{ticket.type}</td>
                        </tr>
                      </tbody>
                    </table>

                    <Link path={PATH_TICKET_VIEW} params={{ id: ticket.id }}>
                      <Button style={{ width: 120 }}>View</Button>
                    </Link>
                  </Space>
                </Box>
              ))
            ) : (
              <Empty message="You have no open ticket" />
            )}
          </Space>
        </Scrollable>
      </Loading>
    </>
  )
}

export default ListOpenTickets
